'use client';

import { useEffect, useRef, useState } from 'react';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { FAVORITES_STEP } from '@eliadmin/core/src/components/dlz/account/favorites/favorites';
import { ProductTile } from '@eliadmin/core/src/components/dlz/product/product-tile';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Two columns, three from the 877 px content column (Figma „Product grid“ 60325:21148). */
const GRID = 'grid grid-cols-2 @[54rem]:grid-cols-3';

/**
 * Dividers of the cell at `index`: the right line between columns and the top line from the second
 * row on, none on the outer edges (Figma: the right and bottom strokes of the cards). The column
 * count is a container query, so every cell gets its classes for both counts – each property has
 * one base and one `@[54rem]` value and they never compete. Lines are border/default on desktop
 * and border/subtle (the card's own stroke) on phones, as in the frames.
 */
function cellLines(index: number) {
  return cn(
    'border-[#E2E4E7] md:border-[#D5D8DC]',
    index % 2 === 0 ? 'border-r' : 'border-r-0',
    index % 3 === 2 ? '@[54rem]:border-r-0' : '@[54rem]:border-r',
    index >= 2 && 'border-t',
    index === 2 && '@[54rem]:border-t-0',
  );
}

/** Focuses the product link of the card at `index`. */
function focusCard(list: HTMLUListElement | null, index: number) {
  list?.children[index]?.querySelector<HTMLElement>('a[href]')?.focus();
}

/**
 * The favourites in the card grid, revealed six at a time by „Zobraziť ďalšie N“ (Figma „Load
 * more“ 60325:21343: secondary button, full width and 48 px on phones). Focus never falls to the
 * page: the first revealed card takes it after „Zobraziť ďalšie“, and when a favourite is removed
 * with its focused heart, the card that moved into its place does.
 */
export function FavoritesGrid({ products }: { products: Product[] }) {
  const { t } = useTranslations();
  const [visible, setVisible] = useState(FAVORITES_STEP);
  const grid = useRef<HTMLUListElement>(null);
  const revealedFrom = useRef<number | null>(null);
  const focused = useRef<{ id: number; index: number } | null>(null);
  const more = Math.min(FAVORITES_STEP, products.length - visible);

  useEffect(() => {
    if (revealedFrom.current === null) return;
    focusCard(grid.current, revealedFrom.current);
    revealedFrom.current = null;
  }, [visible]);

  useEffect(() => {
    const card = focused.current;
    if (!card || products.some(product => product.id === card.id)) return;
    focused.current = null;
    if (document.activeElement === document.body) {
      focusCard(grid.current, Math.min(card.index, products.length - 1));
    }
  }, [products]);

  return (
    <div className="flex flex-col gap-6">
      <ul
        ref={grid}
        className={GRID}
        onFocus={event => {
          const item = (event.target as HTMLElement).closest('li');
          const index = item ? Array.from(event.currentTarget.children).indexOf(item) : -1;
          focused.current = index >= 0 ? { id: products[index].id, index } : null;
        }}
      >
        {products.slice(0, visible).map((product, index) => (
          <li key={product.id} className="flex">
            <ProductTile
              product={product}
              layout="grid"
              className={cn('w-full', cellLines(index))}
            />
          </li>
        ))}
      </ul>
      {more > 0 ? (
        <Button
          variant="secondary"
          className="h-12 w-full @[31rem]:h-[52px] @[31rem]:w-auto @[31rem]:self-center"
          onClick={() => {
            revealedFrom.current = visible;
            setVisible(count => count + FAVORITES_STEP);
          }}
        >
          {t('text1715')} {more}
        </Button>
      ) : null}
    </div>
  );
}

/** Placeholder cards while the list loads (the first step of the grid). */
export function FavoritesGridSkeleton() {
  return (
    <ul aria-hidden className={GRID}>
      {Array.from({ length: FAVORITES_STEP }, (_, index) => (
        <li key={index} className={cn('flex flex-col gap-3 p-3 md:gap-4 md:p-4', cellLines(index))}>
          <Skeleton className="aspect-square w-full rounded-[18px] bg-[#F5F6F7]" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-1/4 bg-[#F5F6F7]" />
            <Skeleton className="h-[46px] w-11/12 bg-[#F5F6F7]" />
            <Skeleton className="h-5 w-1/3 bg-[#F5F6F7]" />
          </div>
          <Skeleton className="h-[52px] w-1/2 bg-[#F5F6F7]" />
        </li>
      ))}
    </ul>
  );
}
